// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { FormControl, InputLabel, Select, MenuItem, TextField, FormGroup, FormControlLabel, Checkbox, Button, Grid, Box, Typography, Divider, Dialog, DialogTitle, DialogContent, DialogContentText, Paper, Accordion, AccordionSummary, AccordionDetails, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import LinearProgress from '@mui/material/LinearProgress';
import { useLocations } from '../../hooks/useLocations';
import { useMachinesByLocation } from '../../hooks/useMachinesByLocation';
import { useTechnicians } from '../../hooks/useTechnicians';
import { saveInspectionReport, uploadFileToStorage, ensureBucketExists, getDownloadURL } from '../../services/supabaseService';
import { generatePDF } from '../../utils/pdfGenerator';
import BuildIcon from '@mui/icons-material/Build';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import SearchIcon from '@mui/icons-material/Search';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import ScaleIcon from '@mui/icons-material/Scale';
import InventoryIcon from '@mui/icons-material/Inventory';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import SendIcon from '@mui/icons-material/Send';
import GetAppIcon from '@mui/icons-material/GetApp';

// Styled components for enhanced visuals
const SectionHeader = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(4),
  background: theme.palette.mode === 'dark' ? '#424242' : theme.palette.primary.main,
  color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.primary.contrastText,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[3],
}));

interface ChecklistItem {
  [key: string]: {
    yes: boolean;
    no: boolean;
    comment: string;
  };
}
const checklistSections = [
  {
    "title": "CLEANING",
    "keys": [
      "STATION FREE OF DUST",
      "HAND SCANNER LENS CLEANED",
      "PAKR GLASS CLEANED"
    ]
  },
  {
    "title": "INSPECTION",
    "keys": [
      "CHECK FOR STRUCTURAL DEFORMITY ON THE FRAME",
      "CHECK ALL BRACKETS ARE FULLY SECURED",
      "CHECK CABLES FOR KINKS AND CUTS, REPLACE IF NECESSARY",
      "CHECK FOR LOOSE CABLE, USE CABLE TIES TO HARNESS THE CABLES",
      "CABLE CONNECTIONS CHECKED"
    ]
  },
  {
    "title": "HAND SCANNER",
    "keys": [
      "TEST SCAN FUNCTIONALITY",
      "CHECK CABLES OR CRADLE TO ENSURE CONNECTIVITY"
    ]
  },
  {
    "title": "DIMENSIONER",
    "keys": [
      "PASS DIMENSIONING TEST WITHIN +/- 0.2in",
      "TEST BOX LANE 1",
      "TEST BOX LANE 2",
      "TEST BOX LANE 3",
      "TEST BOX LANE 4",
      "TEST BOX LANE 5",
      "CHECK ENVISION SOFTWARE VERSION"
    ]
  },
  {
    "title": "SCALE",
    "keys": [
      "PASS WEIGHT TEST WITHIN TOLERANCES"
    ]
  },
  {
    "title": "INVENTORY CHECK",
    "keys": [
      "CHECK OUR INVENTORY LIST (SERIAL NUMBER)"
    ]
  },
  {
    "title": "TLX/CARGOSCAN",
    "keys": [
      "CHECK THE HEX SCREW ON THE BERING",
      "SYSTEM FREE OF DUST",
      "CHECK CABLE CONNECTION"
    ],
  },
  // Add more sections as needed
];

const NewInspection: React.FC = () => {
  const [technician, setTechnician] = useState('');
  const [location, setLocation] = useState('');
  const [selectedStations, setSelectedStations] = useState<Array<{ id: string, name: string }>>([]);
  const [date, setDate] = useState('');
  const [checklist, setChecklist] = useState<ChecklistItem>({});
  const [uploadStatus, setUploadStatus] = useState('');
  const [pdfFile, setPdfFile] = useState('');
  const [fileUrl, setFileUrl] = useState('');
  const [showDownloadButton, setShowDownloadButton] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const theme = useTheme();
  const backgroundColor = theme.palette.mode === 'light' ? '#061ab1' : '#363636';
  const okButtonColor = theme.palette.mode === 'light' ? '#061ab1' : '#363636';
  const [uploadProgress, setUploadProgress] = useState(0);

  const assignees = useTechnicians();
  const locations = useLocations();
  const stations = useMachinesByLocation(location);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setUploadStatus('Uploading...');
    const shortLocation = location.split('-')[0].trim();

    try {
      setUploadProgress(10);

      // Process each selected station
      for (const station of selectedStations) {
        const formData = {
          technician,
          location,
          station: station.id,
          stationName: station.name,
          date,
          checklist
        };

        const fileName = `private/${shortLocation}-${technician}-${station.name}-${date}.pdf`;
        const pdfBlob = generatePDF(formData);

        if (pdfBlob) {
          const bucketName = 'Quarterly_Inspection';

          try {
            const downloadURL = await uploadFileToStorage(bucketName, fileName, pdfBlob);
            setFileUrl(downloadURL);

            await saveInspectionReport({
              location: location,
              machine: station.id,
              machineName: station.name,
              date: date,
              technician: technician,
              checklist: checklist,
              pdfUrl: downloadURL
            });

          } catch (error) {
            console.error(`Error processing station ${station.name}:`, error);
            throw error;
          }
        }
      }

      setUploadProgress(100);
      setUploadStatus('Upload completed!');
      setShowDownloadButton(true);
      setOpenDialog(true);

      // Reset form
      setTechnician('');
      setLocation('');
      setSelectedStations([]);
      setDate('');
      const clearedChecklist = Object.keys(checklist).reduce((acc, key) => {
        acc[key] = { yes: false, no: false, comment: '' };
        return acc;
      }, {});
      setChecklist(clearedChecklist);

      setTimeout(() => setUploadProgress(0), 3000);

    } catch (error) {
      console.error('Failed to upload or save inspection report:', error);
      let errorMessage = 'Upload failed. Please try again.';

      if (error.message?.includes('row-level security policy')) {
        errorMessage = 'Permission denied due to RLS policy. Please contact your administrator.';
      } else if (error.statusCode === '403' || error.error === 'Unauthorized') {
        errorMessage = 'Authorization failed. The service role key might not have sufficient permissions.';
      } else if (error.message?.includes('bucket')) {
        errorMessage = 'Storage bucket not found. Please contact your administrator to create the required storage bucket.';
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }

      setUploadStatus(errorMessage);
      setUploadProgress(0);
      alert(`Upload failed: ${errorMessage}`);
    }
  };

  const handleChecklistChange = (key: string, field: 'yes' | 'no' | 'comment', value: boolean | string) => {
    setChecklist(prevState => ({
      ...prevState,
      [key]: {
        ...prevState[key],
        [field]: value,
      },
    }));
  };

  const handleStatusChange = (key: string, newStatus: 'yes' | 'no' | null) => {
    setChecklist(prevState => ({
      ...prevState,
      [key]: {
        ...prevState[key],
        yes: newStatus === 'yes',
        no: newStatus === 'no',
        comment: prevState[key]?.comment || ''
      }
    }));
  };

  const handleMarkAllYes = (sectionKeys: string[]) => {
    setChecklist(prevState => {
      const newState = { ...prevState };
      sectionKeys.forEach(key => {
        newState[key] = {
          yes: true,
          no: false,
          comment: newState[key]?.comment || ''
        };
      });
      return newState;
    });
  };

  const handleMarkAllNo = (sectionKeys: string[]) => {
    setChecklist(prevState => {
      const newState = { ...prevState };
      sectionKeys.forEach(key => {
        newState[key] = {
          yes: false,
          no: true,
          comment: newState[key]?.comment || ''
        };
      });
      return newState;
    });
  };

  // Helper function to get the appropriate icon for each section
  const getSectionIcon = (title) => {
    switch (title) {
      case 'CLEANING':
        return <CleaningServicesIcon sx={{ mr: 2, fontSize: 28 }} />;
      case 'INSPECTION':
        return <SearchIcon sx={{ mr: 2, fontSize: 28 }} />;
      case 'HAND SCANNER':
        return <BuildIcon sx={{ mr: 2, fontSize: 28 }} />;
      case 'DIMENSIONER':
        return <AspectRatioIcon sx={{ mr: 2, fontSize: 28 }} />;
      case 'SCALE':
        return <ScaleIcon sx={{ mr: 2, fontSize: 28 }} />;
      case 'INVENTORY CHECK':
        return <InventoryIcon sx={{ mr: 2, fontSize: 28 }} />;
      case 'TLX/CARGOSCAN':
        return <SettingsIcon sx={{ mr: 2, fontSize: 28 }} />;
      default:
        return <BuildIcon sx={{ mr: 2, fontSize: 28 }} />;
    }
  };

  const generateChecklistItems = () => {
    return checklistSections.map((section) => (
      <Accordion
        key={section.title}
        defaultExpanded={true}
        sx={{
          mb: 2,
          borderRadius: '12px !important',
          '&:before': { display: 'none' }, // Remove default divider
          boxShadow: theme.shadows[1],
          overflow: 'hidden'
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : theme.palette.grey[50],
            borderBottom: `1px solid ${theme.palette.divider}`,
            '& .MuiAccordionSummary-content': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {getSectionIcon(section.title)}
            <Typography variant="h6" fontWeight="bold">{section.title}</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
            <Button
              variant="outlined"
              size="small"
              color="success"
              startIcon={<DoneAllIcon />}
              onClick={(e) => {
                e.stopPropagation();
                handleMarkAllYes(section.keys);
              }}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Mark All Yes
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="error"
              startIcon={<HighlightOffIcon />}
              onClick={(e) => {
                e.stopPropagation();
                handleMarkAllNo(section.keys);
              }}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Mark All No
            </Button>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3 }}>
          {section.keys.map((key) => {
            const status = checklist[key]?.yes ? 'yes' : checklist[key]?.no ? 'no' : null;
            return (
              <Grid container spacing={2} key={key} alignItems="center" sx={{ mb: 2, pb: 2, borderBottom: `1px solid ${theme.palette.divider}`, '&:last-child': { borderBottom: 'none', mb: 0, pb: 0 } }}>
                <Grid item xs={12} md={5}>
                  <Typography fontWeight="medium">{key}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <ToggleButtonGroup
                    value={status}
                    exclusive
                    onChange={(e, newVal) => {
                      if (newVal) handleStatusChange(key, newVal);
                    }}
                    fullWidth
                    size="small"
                  >
                    <ToggleButton
                      value="yes"
                      color="success"
                      sx={{
                        '&.Mui-selected': {
                          bgcolor: theme.palette.success.main + '20',
                          color: theme.palette.success.main,
                          borderColor: theme.palette.success.main,
                          '&:hover': { bgcolor: theme.palette.success.main + '30' }
                        }
                      }}
                    >
                      <CheckIcon sx={{ mr: 1, fontSize: 18 }} /> Yes
                    </ToggleButton>
                    <ToggleButton
                      value="no"
                      color="error"
                      sx={{
                        '&.Mui-selected': {
                          bgcolor: theme.palette.error.main + '20',
                          color: theme.palette.error.main,
                          borderColor: theme.palette.error.main,
                          '&:hover': { bgcolor: theme.palette.error.main + '30' }
                        }
                      }}
                    >
                      <CloseIcon sx={{ mr: 1, fontSize: 18 }} /> No
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Add comment..."
                    variant="outlined"
                    value={checklist[key]?.comment || ''}
                    onChange={e => handleChecklistChange(key, 'comment', e.target.value)}
                  />
                </Grid>
              </Grid>
            );
          })}
        </AccordionDetails>
      </Accordion>
    ));
  };

  return (
    <Box width="100%" padding={3}>
      <Typography variant="h4" marginBottom={2} fontWeight="bold" color="text.primary">New Inspection</Typography>
      <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4 }}>
        Complete the inspection form below for the selected station.
      </Typography>
      <Paper elevation={0} sx={{
        p: { xs: 2, md: 4 },
        mb: 4,
        borderRadius: 4,
        border: '1px solid',
        borderColor: 'divider',
        background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : '#fff',
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
      }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Technician</InputLabel>
              <Select
                value={technician}
                label="Technician"
                onChange={(e) => setTechnician(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                {assignees.map((assignee) => (
                  <MenuItem key={assignee} value={assignee}>{assignee}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Location</InputLabel>
              <Select
                value={location}
                label="Location"
                onChange={(e) => setLocation(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                {locations.map((loc) => (
                  <MenuItem key={loc} value={loc}>{loc}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Station</InputLabel>
              <Select
                multiple
                value={selectedStations.map(station => station.id)}
                label="Station"
                onChange={(e) => {
                  const selectedIds = e.target.value as string[];
                  const selectedMachines = stations
                    .filter(machine => selectedIds.includes(machine.id))
                    .map(machine => ({ id: machine.id, name: machine.name }));
                  setSelectedStations(selectedMachines);
                }}
                renderValue={(selected) => {
                  const selectedNames = selectedStations.map(station => station.name);
                  return selectedNames.join(', ');
                }}
                sx={{ borderRadius: 2 }}
              >
                {stations.map((machine) => (
                  <MenuItem key={machine.id} value={machine.id}>
                    <Checkbox checked={selectedStations.some(station => station.id === machine.id)} />
                    {machine.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Date"
              type="date"
              fullWidth
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ mb: 4 }}>
        {generateChecklistItems()}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 6 }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleSubmit}
          startIcon={<SendIcon />}
          sx={{
            px: 8,
            py: 1.5,
            borderRadius: '12px',
            fontSize: '1.1rem',
            textTransform: 'none',
            fontWeight: 'bold',
            boxShadow: theme.shadows[4],
            '&:hover': {
              boxShadow: theme.shadows[8],
              transform: 'translateY(-2px)'
            },
            transition: 'all 0.2s'
          }}
        >
          Submit Inspection Report
        </Button>
      </Box>
      <Dialog
        open={uploadStatus === 'Uploading...'}
        aria-labelledby="upload-progress-dialog-title"
      >
        <DialogTitle id="upload-progress-dialog-title">Uploading Report</DialogTitle>
        <DialogContent>
          <LinearProgress variant="determinate" value={uploadProgress} />
        </DialogContent>
      </Dialog>
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        aria-labelledby="upload-confirm-dialog"
        aria-describedby="upload-confirm-dialog-description"
      >
        <DialogTitle id="upload-confirm-dialog">{"Upload Successful"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="upload-confirm-dialog-description">
            Your inspection report has been successfully uploaded.
          </DialogContentText>
        </DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, p: 2 }}>
          {showDownloadButton && fileUrl && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<GetAppIcon />}
              onClick={async () => {
                try {
                  const downloadUrl = await getDownloadURL('Quarterly_Inspection', fileUrl);
                  window.open(downloadUrl, '_blank');
                } catch (error) {
                  console.error('Failed to generate download URL:', error);
                  alert('Failed to download PDF. Please try again.');
                }
              }}
            >
              Download PDF
            </Button>
          )}
          <Button
            variant="contained"
            onClick={() => {
              setOpenDialog(false);
              setShowDownloadButton(false);
            }}
          >
            OK
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
};

export default NewInspection;